import type { Booking, BookingsApiOptions, BookingsApiPayload } from '@brighterly/lib-core-types/booking/interfaces'

import { SortDirection } from '@brighterly/lib-core-types/options/enums'

import { listBookingRequestStatuses, listFallbackBookingRequestStatuses } from '@shared/lib/booking-status'
import { useCsRoutes } from '@shared/api'
import { isFullArray } from '@shared/lib/collections'
import { pick } from '@shared/lib/collections'

import { useBookingStore } from '../model/store/store'

export const useBookingsApiDemo = async (payload: BookingsApiPayload, options?: BookingsApiOptions): Promise<Booking[] | null> => {
  const { $axios } = useNuxtApp()

  const bookingStore = useBookingStore()

  let requestId: number | null = null

  const setStart = (): void => {
    options?.onStart?.()

    if (payload.skipStore) return

    const isRefresh = payload.background && bookingStore.getListStatus(payload.disciplineId) === 'ready'
    requestId = bookingStore.beginList(payload.disciplineId, isRefresh ? 'refresh' : 'enter')
    if (!isRefresh) bookingStore.setCountdown(null)
  }

  const setSuccess = (bookings: Booking[]): void => {
    if (requestId !== null) {
      bookingStore.settleList(payload.disciplineId, requestId, bookings)
      bookingStore.setBookingTrigger()
    }

    options?.onSuccess?.(bookings)
  }

  const setError = (err: any): void => {
    console.error(err)

    if (requestId !== null) bookingStore.failList(payload.disciplineId, requestId)
  }

  const url = useCsRoutes('bookings', pick({
    '[discipline_id]': payload.disciplineId,
    status: listBookingRequestStatuses,
    with_delayed_canceled: 0,
    sort_by: ['start', 'id'],
    sort_direction: SortDirection.Asc,
    from: payload.from,
    to: payload.to,
  }))

  setStart()

  try {
    const { data } = await $axios.get(url!)
    const bookings = data.data?.data || []

    if (isFullArray(bookings)) {
      setSuccess(bookings)
      return bookings
    } else {
      const url = useCsRoutes('bookings', pick({
        '[discipline_id]': payload.disciplineId,
        status: listFallbackBookingRequestStatuses,
        with_delayed_canceled: 0,
        sort_by: ['start', 'id'],
        sort_direction: SortDirection.Desc,
        from: payload.from,
        to: payload.to,
      }))

      try {
        const { data } = await $axios.get(url!)
        const bookings = data.data?.data || []

        setSuccess(bookings)
        return bookings
      } catch (err) {
        setError(err)
        return null
      }
    }
  } catch (err) {
    setError(err)
    return null
  }
}
